import { type DeviceDefinitionDto, useDeviceDefinitions } from "@repo/data-store";
import { EmptyState } from "@repo/ui/components/empty-state";
import { MarkdownContent } from "@repo/ui/components/markdown-content";
import { Badge } from "@repo/ui/components/ui/badge";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Boxes, Plus } from "lucide-react";
import { CATALOG_STATUS_OPTIONS } from "@/features/catalog/lib/catalog-status-options";
import { deviceDefinitionRouteId } from "@/features/catalog/lib/device-definition-route";
import { ActionButton } from "@/shared/common/action-button";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";
import { SearchFilterBar } from "@/shared/common/search-filter-bar";
import { formatDate } from "@/shared/lib/date-formats";
import { StatusPickerBadge } from "@/shared/status/status-picker-badge";

/**
 * The devices tab. A device here is the DEFINITION — a versioned catalog
 * lineage with parameters, activate/deactivate and a revision history — and its
 * physical units are listed and added on its own page, as instances of it.
 * There is no second tab for the units: Devices beside Device kinds asked the
 * reader to know a distinction the word "device" does not carry.
 */
export const Route = createFileRoute("/catalog/_layout/devices")({
  component: DevicesComponent,
  validateSearch: (search: Record<string, unknown>): { q?: string; status?: string } => ({
    q: (search.q as string) || undefined,
    status: (search.status as string) || undefined,
  }),
});

function statusOf(device: DeviceDefinitionDto): "active" | "retired" {
  return device.availability === "Active" ? "active" : "retired";
}

function productBadge(device: DeviceDefinitionDto) {
  const label = [device.manufacturer, device.modelNumber].filter(Boolean).join(" · ");
  return label ? (
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal text-muted-foreground">
      {label}
    </Badge>
  ) : null;
}

function parameterCount(device: DeviceDefinitionDto) {
  return device.parameters.length === 0 ? null : (
    <span className="text-xs text-muted-foreground text-right">
      {device.parameters.length} {device.parameters.length === 1 ? "parameter" : "parameters"}
    </span>
  );
}

function DevicesComponent() {
  const { q: search = "", status: statusFilter } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { permissions } = Route.useRouteContext();
  const setSearch = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });
  const setStatusFilter = (value: string | null) =>
    navigate({ search: (prev) => ({ ...prev, status: value || undefined }), replace: true });
  const canCreate = permissions.canWrite("DeviceDefinition");

  const devices = useDeviceDefinitions({
    search,
    ...(statusFilter ? { availability: statusFilter === "active" ? ("Active" as const) : ("Inactive" as const) } : {}),
  });

  const groups: GroupedListGroup<DeviceDefinitionDto>[] = [
    {
      id: "devices",
      name: "Devices",
      items: devices,
      headerAction: canCreate
        ? () => (
            <ActionButton state={{ allowed: true }} variant="ghost" size="icon-xs" to="/catalog/devices/new">
              <Plus className="size-4" />
            </ActionButton>
          )
        : undefined,
    },
  ];

  return (
    <GroupedList
      groups={groups}
      isLoading={false}
      header={
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search devices..."
          statusOptions={[
            { value: "active", label: "Active" },
            { value: "retired", label: "Retired" },
          ]}
          statusValue={statusFilter ?? null}
          onStatusChange={setStatusFilter}
        />
      }
      columns={[
        { id: "title", width: "1fr" },
        { id: "parameters", width: "auto" },
        { id: "date", width: "auto" },
        { id: "status", width: "100px" },
      ]}
      columnGap="4px"
      renderCells={(device) => [
        <div key="title" className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-medium text-sm">{device.name}</span>
            {productBadge(device)}
          </div>
          {device.description && (
            <MarkdownContent className="text-xs text-muted-foreground line-clamp-1">
              {device.description}
            </MarkdownContent>
          )}
        </div>,
        parameterCount(device),
        <span key="date" className="text-xs text-muted-foreground text-right">
          {formatDate(device.mintedAt)}
        </span>,
        <div key="status" className="flex justify-end">
          <StatusPickerBadge value={statusOf(device)} options={CATALOG_STATUS_OPTIONS} onChange={() => {}} disabled />
        </div>,
      ]}
      wrapItem={(device, children) => (
        <Link
          to="/catalog/devices/$deviceId"
          params={{ deviceId: deviceDefinitionRouteId(device.definitionUrl) }}
          className="cursor-pointer"
        >
          {children}
        </Link>
      )}
      renderItem={(device) => (
        <div className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer">
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-medium text-sm">{device.name}</span>
              {productBadge(device)}
            </div>
            {device.description && (
              <MarkdownContent className="text-xs text-muted-foreground line-clamp-1">
                {device.description}
              </MarkdownContent>
            )}
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="text-xs text-muted-foreground">{formatDate(device.mintedAt)}</span>
            <StatusPickerBadge value={statusOf(device)} options={CATALOG_STATUS_OPTIONS} onChange={() => {}} disabled />
          </div>
        </div>
      )}
      getItemKey={(device) => device.definitionUrl}
      emptyMessage="No devices found"
      isFiltered={!!search || !!statusFilter}
      emptyState={
        <EmptyState className="m-auto">
          <EmptyState.Icon>
            <Boxes className="size-4" />
          </EmptyState.Icon>
          <EmptyState.Content>
            <EmptyState.Title>No devices yet</EmptyState.Title>
            <EmptyState.Description>
              A device is the product model a procedure names, and whose parameters it inherits.
            </EmptyState.Description>
          </EmptyState.Content>
          {canCreate && (
            <EmptyState.Actions>
              <ActionButton state={{ allowed: true }} variant="outline" size="sm" to="/catalog/devices/new">
                <Plus className="size-4 mr-1" />
                Device
              </ActionButton>
            </EmptyState.Actions>
          )}
        </EmptyState>
      }
    />
  );
}
